/** Prompt variable groups for Welcome org LLM prompt editor (shared with AI report widgets). */

const SCOPE_SYSTEM_ITEMS = [
  {
    token: "{known_pivot_key}",
    descriptionKey: "welcome.promptVarDesc_knownPivotKey",
  },
  {
    token: "{known_pivot_value}",
    descriptionKey: "welcome.promptVarDesc_knownPivotValue",
  },
];

const TOPIC_SCOPE_ITEM = {
  token: "{topic_label}",
  descriptionKey: "welcome.promptVarDesc_topicLabel",
};

const MAP_REDUCE_SYSTEM_ITEMS = [
  {
    token: "{reviews_block}",
    descriptionKey: "welcome.promptVarDesc_reviewsBlock",
  },
  {
    token: "{chunk_id}",
    descriptionKey: "welcome.promptVarDesc_chunkId",
  },
  {
    token: "{reviews_content}",
    descriptionKey: "welcome.promptVarDesc_reviewsContent",
  },
  {
    token: "{aggregated_map_outputs}",
    descriptionKey: "welcome.promptVarDesc_aggregatedMapOutputs",
  },
];

const DATE_USER_ITEMS = [
  {
    token: "{dashboard_id}",
    descriptionKey: "welcome.promptVarDesc_dashboardId",
  },
  { token: "{since}", descriptionKey: "welcome.promptVarDesc_since" },
  { token: "{until}", descriptionKey: "welcome.promptVarDesc_until" },
];

const PROMPT_VARIABLE_GROUPS_DEFAULT = [
  {
    key: "system",
    titleKey: "welcome.promptVarsSystem",
    items: [...SCOPE_SYSTEM_ITEMS, TOPIC_SCOPE_ITEM, ...MAP_REDUCE_SYSTEM_ITEMS],
  },
  {
    key: "user",
    titleKey: "welcome.promptVarsUser",
    items: [
      ...DATE_USER_ITEMS,
      {
        token: "{adultCount}",
        descriptionKey: "welcome.promptVarDesc_adultCount",
      },
    ],
  },
];

const PROMPT_VARIABLE_GROUPS_BY_METRIC = {
  /** Root Cause (20): topic scope via topic_label; hotel scope via known_pivot_* */
  20: [
    {
      key: "system",
      titleKey: "welcome.promptVarsSystem",
      items: [
        ...SCOPE_SYSTEM_ITEMS,
        TOPIC_SCOPE_ITEM,
        ...MAP_REDUCE_SYSTEM_ITEMS,
      ],
    },
    {
      key: "user",
      titleKey: "welcome.promptVarsUser",
      items: DATE_USER_ITEMS,
    },
  ],
  21: [
    {
      key: "system",
      titleKey: "welcome.promptVarsSystem",
      items: [
        ...SCOPE_SYSTEM_ITEMS,
        TOPIC_SCOPE_ITEM,
        ...MAP_REDUCE_SYSTEM_ITEMS,
      ],
    },
    {
      key: "user",
      titleKey: "welcome.promptVarsUser",
      items: DATE_USER_ITEMS,
    },
  ],
  23: [
    {
      key: "system",
      titleKey: "welcome.promptVarsSystem",
      items: [
        ...SCOPE_SYSTEM_ITEMS,
        TOPIC_SCOPE_ITEM,
        ...MAP_REDUCE_SYSTEM_ITEMS,
        {
          token: "{key_drivers_table}",
          descriptionKey: "welcome.promptVarDesc_keyDriversTable",
        },
      ],
    },
    {
      key: "user",
      titleKey: "welcome.promptVarsUser",
      items: DATE_USER_ITEMS,
    },
  ],
  /** Highlights: no key_drivers_table — wrong surface; worker skips that token for metric 25. */
  25: [
    {
      key: "system",
      titleKey: "welcome.promptVarsSystem",
      items: [
        ...SCOPE_SYSTEM_ITEMS,
        TOPIC_SCOPE_ITEM,
        ...MAP_REDUCE_SYSTEM_ITEMS,
        {
          token: "{reviews_with_pivots_embedded}",
          descriptionKey: "welcome.promptVarDesc_reviewsWithPivotsEmbedded",
        },
      ],
    },
    {
      key: "user",
      titleKey: "welcome.promptVarsUser",
      items: DATE_USER_ITEMS,
    },
  ],
};

export function getPromptVariableGroupsForMetric(metricId) {
  return (
    PROMPT_VARIABLE_GROUPS_BY_METRIC[Number(metricId)] || PROMPT_VARIABLE_GROUPS_DEFAULT
  );
}

/** Legacy aliases — not shown in the variable panel. */
const ADDITIONAL_ALLOWED_PROMPT_TOKENS = [
  "{hotel_name}",
  "{otel_adi}",
  "{brand_name}",
  "{property_name}",
  "{kategori_adi}",
  "{category_name}",
  "{topic_name}",
];

/** Same token set used for org prompt validation (sidebar + worker aliases). */
export function getAllowedPromptTokenSetForMetric(metricId) {
  const set = new Set(ADDITIONAL_ALLOWED_PROMPT_TOKENS);
  const groups = getPromptVariableGroupsForMetric(metricId);
  for (const g of groups) {
    for (const item of g.items) {
      if (item?.token) {
        set.add(item.token);
      }
    }
  }
  return set;
}

const BRACE_PLACEHOLDER_RE = /\{[^{}]+\}/g;

/**
 * Worker / UI prompt tokens look like `{snake_case}` or `{otel_adi}` (no spaces, no JSON).
 * This filters false positives from JSON examples and prose like "ilk karakter { , son }".
 */
const PLACEHOLDER_NAME_BODY_RE = /^[a-z][a-z0-9_]*$/i;

export function looksLikeNamedPromptPlaceholder(token) {
  if (token == null || token.length < 3 || !token.startsWith("{") || !token.endsWith("}")) {
    return false;
  }
  const inner = token.slice(1, -1).trim();
  return PLACEHOLDER_NAME_BODY_RE.test(inner);
}

/**
 * `{name}`-style segments: unknown vs `allowedSet`, first occurrence order, deduped.
 * Ignores `{...}` spans that don't look like worker placeholders (e.g. JSON snippets).
 */
export function listUnknownPromptBraceTokens(text, allowedSet) {
  if (text == null || text === "") {
    return [];
  }
  const ordered = [];
  const inList = new Set();
  const re = new RegExp(BRACE_PLACEHOLDER_RE.source, "g");
  let m;
  while ((m = re.exec(text)) !== null) {
    const token = m[0];
    if (!looksLikeNamedPromptPlaceholder(token)) {
      continue;
    }
    if (allowedSet.has(token) || inList.has(token)) {
      continue;
    }
    inList.add(token);
    ordered.push(token);
  }
  return ordered;
}
